import {
  useDebounceFn,
  useEventListener,
  useResizeObserver,
  useThrottleFn,
} from "@vueuse/core";
import * as d3 from "d3-geo";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
  type ComputedRef,
  type Ref,
} from "vue";

import type { THoverGeoPoint } from "./gridHoverUtils.ts";
import {
  altitudeToCameraDistance,
  EARTH_RADIUS_METERS,
  type TCameraState,
  type TCameraUrlState,
  type TGridCameraState,
} from "./useGridCameraState.ts";
import { useGridSnapshot } from "./useGridSnapshot.ts";

import {
  CAMERA_VERTICAL_FOV_DEGREES,
  getGlobeFitCameraDistance,
  getCameraDistanceForVerticalSpan,
  getGlobeMovementScale,
  getVisibleVerticalSpan,
} from "@/lib/camera/cameraSettings.ts";
import {
  handleKeyDown,
  useSurfaceZoom,
} from "@/lib/camera/OrbitControlsAddOn.ts";
import {
  createPointPivot,
  pivotNearPlane,
  surfacePointNearRay,
  wheelDollyScale,
  type TPivotLeftDrag,
  type TPointPivot,
} from "@/lib/camera/pointPivot.ts";
import { getRegionalCameraPosition } from "@/lib/camera/regionalCamera.ts";
import {
  distanceScaleOf,
  getDistanceScale,
  metersPerPixelAt,
} from "@/lib/projection/distanceScale.ts";
import {
  isAzimuthalProjectionType,
  MERCATOR_LAT_LIMIT,
  PROJECTION_TYPES,
  ProjectionHelper,
  type TProjectionCenter,
} from "@/lib/projection/projectionUtils.ts";
import { viewFootprint } from "@/lib/projection/viewFootprint.ts";
import { useUrlParameterStore } from "@/store/paramStore.ts";
import { useGlobeControlStore } from "@/store/store.ts";
import { isDisplayMode, isPresenterActive } from "@/store/usePresenterSync.ts";
import {
  CONTROL_PANEL_WIDTH,
  MOBILE_BREAKPOINT,
} from "@/ui/common/viewConstants.ts";

type UseGridSceneOptions = {
  projectionHelper: ComputedRef<ProjectionHelper>;
  projectionCenter: Ref<TProjectionCenter | undefined>;
  controlPanelVisible: Ref<boolean>;
  cameraState: TGridCameraState;
  onMotionStateChange?: (isInMotion: boolean) => void;
  onReady?: () => void | Promise<void>;
};

const DEFAULT_PROJECTION_CENTER: TProjectionCenter = { lat: 0, lon: 0 };
const PROJECTION_CENTER_PRECISION = 10_000;

function roundProjectionCenter(value: number) {
  return (
    Math.round(value * PROJECTION_CENTER_PRECISION) /
    PROJECTION_CENTER_PRECISION
  );
}

/* eslint-disable-next-line max-lines-per-function */
export function useGridScene(options: UseGridSceneOptions) {
  const {
    projectionHelper,
    projectionCenter,
    controlPanelVisible,
    cameraState,
    onMotionStateChange,
    onReady,
  } = options;

  const store = useGlobeControlStore();
  const urlParameterStore = useUrlParameterStore();
  let initialRegionalFitPending =
    !cameraState.decodeCameraFromURL() &&
    urlParameterStore.paramLat === undefined &&
    urlParameterStore.paramLon === undefined &&
    !isDisplayMode.value &&
    !isPresenterActive.value;

  const canvas: Ref<HTMLCanvasElement | undefined> = ref();
  const box: Ref<HTMLDivElement | undefined> = ref();
  const width: Ref<number | undefined> = ref(undefined);
  const height: Ref<number | undefined> = ref(undefined);
  const frameId = ref(0);

  let scene: THREE.Scene | undefined = undefined;
  let camera: THREE.PerspectiveCamera | undefined = undefined;
  let renderer: THREE.WebGLRenderer | undefined = undefined;
  let orbitControls: OrbitControls | undefined = undefined;
  // the point tier's camera: a pivot on the ground (pointPivot.ts), wanted
  // by the point layer and applied once the controls exist and the globe is
  // drawn
  let pivotControls: TPointPivot | undefined = undefined;
  let pivotWanted = false;
  const pivotGround = shallowRef<{ lat: number; lon: number } | null>(null);
  let updateLOD: (() => void) | undefined = undefined;
  let baseSurface: THREE.Mesh | undefined = undefined;
  let pickSurface: THREE.Mesh | undefined = undefined;
  let mouseDown = false;
  let wheelActive = false;
  const raycaster = new THREE.Raycaster();
  const GLOBE_SPHERE = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1);
  const lastRenderedCameraPosition = new THREE.Vector3();
  const lastRenderedCameraQuaternion = new THREE.Quaternion();
  const hoveredGeoPoint = shallowRef<THoverGeoPoint | null>(null);
  // the place last clicked or tapped (not dragged), whatever the hover state
  const clickedGeoPoint = shallowRef<THoverGeoPoint | null>(null);
  let clickStart: { clientX: number; clientY: number } | null = null;
  let lastPointerPosition: { clientX: number; clientY: number } | null = null;
  let touchPickStart: { clientX: number; clientY: number } | null = null;
  let touchPickMoved = false;

  let projectionDragActive = false;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragStartCenterLon = 0;
  let dragStartCenterLat = 0;
  let init = true;
  let currentOffset = 0;
  // Counts consecutive frames where OrbitControls reported no camera change.
  // The loop keeps running until this reaches IDLE_FRAMES_BEFORE_STOP so that
  // the damping delta in OrbitControls is fully drained to zero before we
  // stop calling update(). Without this, residual velocity would be applied
  // the next time anything triggers a render (click, bounds change, etc.).
  let idleFrameCount = 0;
  const IDLE_FRAMES_BEFORE_STOP = 30; // ~500 ms at 60 fps – outlasts any realistic damping
  const WHEEL_END_DELAY_MS = 120;
  const VIEW_FOOTPRINT_INTERVAL_MS = 200;
  const debouncedEndWheelInteraction = useDebounceFn(() => {
    wheelActive = false;
    animationLoop();
  }, WHEEL_END_DELAY_MS);
  const FLAT_CROP_RENDER_ORDER = 5;
  const FLAT_BOUNDARY_STEP_DEGREES = 0.25;
  const TOUCH_PICK_TAP_MAX_DISTANCE_PX = 10;
  // Keep ~1.3 km clearance: close enough for automatic level selection to
  // reach cells of a few metres. Float32 positions resolve about half a
  // metre on the globe, so cell edges get ragged this close; closer views
  // need camera-relative positions (or analytic sphere rendering).
  const GLOBE_MIN_CAMERA_DISTANCE = 1.0002;
  let targetOffset = 0;
  let isInMotion = false;
  let updatingProjectionCenterFromCamera = false;
  let lastAnimationTime: number | undefined = undefined;
  const animationCallbacks = new Set<(deltaSeconds: number) => void>();

  function setMotionState(next: boolean) {
    if (isInMotion === next) {
      return;
    }
    isInMotion = next;
    onMotionStateChange?.(next);
  }

  function getScene() {
    return scene;
  }

  function getCamera() {
    return camera;
  }

  function getRenderer() {
    return renderer;
  }

  function getOrbitControls() {
    return orbitControls;
  }

  function getBaseSurface() {
    return baseSurface;
  }

  function registerUpdateLOD(func: () => void) {
    updateLOD = func;
  }

  function registerAnimationCallback(callback: (deltaSeconds: number) => void) {
    animationCallbacks.add(callback);
    lastAnimationTime = undefined;
    animationLoop();
    return () => {
      animationCallbacks.delete(callback);
      if (animationCallbacks.size === 0) {
        lastAnimationTime = undefined;
      }
    };
  }

  function redraw() {
    if (store.isRotating || projectionDragActive || mouseDown || wheelActive) {
      return;
    }
    render();
  }

  function render() {
    if (updateLOD) {
      updateLOD();
    }
    getOrbitControls()?.update();
    // OrbitControls can report movement forever when rounding at minDistance.
    // Ignore sub-millimetre position noise so settling and URL saves complete.
    const controlsUpdated = camera
      ? lastRenderedCameraPosition.distanceToSquared(camera.position) > 1e-20 ||
        1 - Math.abs(lastRenderedCameraQuaternion.dot(camera.quaternion)) >
          1e-12
      : false;
    if (camera) {
      lastRenderedCameraPosition.copy(camera.position);
      lastRenderedCameraQuaternion.copy(camera.quaternion);
    }
    if (camera) {
      const isFlat = projectionHelper.value.isFlat;
      const altitude = isFlat
        ? camera.position.z
        : camera.position.length() - 1;
      // on the pivot camera the cloud reaches up to the camera
      const near = pivotControls?.active
        ? pivotNearPlane(altitude)
        : Math.min(isFlat ? 0.005 : 0.1, altitude / 2);
      if (camera.near !== near) {
        camera.near = near;
        camera.updateProjectionMatrix();
      }
    }
    getRenderer()?.render(getScene()!, getCamera()!);
    refreshDistanceScale();
    refreshViewFootprint();
    return controlsUpdated;
  }

  // What the camera sees and at what scale: level selection and grids that
  // load only the view follow this; a few times a second is enough.
  const refreshViewFootprint = useThrottleFn(
    () => {
      if (!camera || !canvas.value) {
        return;
      }
      const rect = canvas.value.getBoundingClientRect();
      const next = viewFootprint(camera, projectionHelper.value, rect);
      if (JSON.stringify(next) !== JSON.stringify(store.viewFootprint)) {
        store.viewFootprint = next;
      }
      // The scale is taken at the point of the surface below the camera, as
      // the coarser of the two screen directions: a level sized by the finer
      // one would be far too fine where a flat map is stretched one way. On
      // the pivot camera it is the pivot's: tilted, the ground below the
      // camera is off the view.
      let scale = pivotControls?.active
        ? pivotControls.metersPerPixel(rect.height)
        : null;
      const below = projectionHelper.value.isFlat
        ? new THREE.Vector3(camera.position.x, camera.position.y, 0)
        : camera.position.clone().normalize();
      below.project(camera);
      for (const vertical of scale === null ? [false, true] : []) {
        const along = metersPerPixelAt(
          camera,
          projectionHelper.value,
          rect,
          rect.left + ((below.x + 1) * rect.width) / 2,
          rect.top + ((1 - below.y) * rect.height) / 2,
          EARTH_RADIUS_METERS,
          vertical
        );
        scale = along !== null && along > (scale ?? 0) ? along : scale;
      }
      if (scale !== null) {
        store.metersPerPixel = scale;
      }
      refreshPivotGround();
    },
    VIEW_FOOTPRINT_INTERVAL_MS,
    true
  );

  function refreshPivotGround() {
    const target = orbitControls?.target;
    const next =
      pivotControls?.active && target
        ? ProjectionHelper.cartesianToLatLon(target.x, target.y, target.z)
        : null;
    if (JSON.stringify(next) !== JSON.stringify(pivotGround.value)) {
      pivotGround.value = next;
    }
  }

  /** Enter or leave the pivot camera as wanted, on the globe only. */
  function applyPointPivot() {
    const cam = camera;
    if (!cam || !pivotControls) {
      return;
    }
    const wanted = pivotWanted && !projectionHelper.value.isFlat;
    if (wanted && !pivotControls.active) {
      const forward = cam.getWorldDirection(new THREE.Vector3());
      pivotControls.enter(surfacePointNearRay(cam.position, forward));
    } else if (!wanted && pivotControls.active) {
      pivotControls.leave();
      syncProjectionCenterFromCamera(cam);
      cameraState.encodeCameraToURL(cam, false);
    } else {
      return;
    }
    refreshPivotGround();
    redraw();
  }

  let stopTilt: (() => void) | undefined;
  const pointPivot = {
    enter() {
      pivotWanted = true;
      applyPointPivot();
    },
    leave() {
      pivotWanted = false;
      applyPointPivot();
    },
    /** Animate the tilt to `degrees` from nadir (the Top and Oblique buttons). */
    tiltTo(degrees: number) {
      if (!pivotControls?.active) {
        return;
      }
      stopTilt?.();
      const animation = pivotControls.tiltTo(degrees);
      stopTilt = registerAnimationCallback((deltaSeconds) => {
        if (!animation.advance(deltaSeconds)) {
          stopTilt?.();
          stopTilt = undefined;
        }
      });
    },
    /** Lift the pivot off the sphere by `above` scene units. */
    setHeight(above: number) {
      pivotControls?.setHeight(above);
    },
    /** What a plain left drag does on the pivot. */
    setLeftDrag(mode: TPivotLeftDrag) {
      pivotControls?.setLeftDrag(mode);
    },
    /** The pivot's place while the camera is on it, a few times a second. */
    ground: pivotGround,
    /** The pivot's direction, a unit vector, exact; undefined off it. */
    direction: () =>
      pivotControls?.active && orbitControls
        ? orbitControls.target.clone().normalize()
        : undefined,
    /** The camera's distance to the pivot, scene units; undefined off it. */
    distance: () =>
      pivotControls?.active ? pivotControls.pivotDistance() : undefined,
  };

  /**
   * The distance scale under the cursor, at the canvas centre while the
   * cursor is off it; on the pivot camera, at the pivot itself, so that the tier's scale bar reads the ground scale at the
   * pivot's depth whether or not the pointer is over the canvas (not the
   * ground behind the lifted pivot).
   */
  function refreshDistanceScale() {
    const rect = canvas.value?.getBoundingClientRect();
    if (pivotControls?.active && rect) {
      store.distanceScale = distanceScaleOf(
        pivotControls.metersPerPixel(rect.height)
      );
      return;
    }
    const at =
      lastPointerPosition ??
      (rect && {
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2,
      });
    store.distanceScale =
      at && camera && rect
        ? getDistanceScale(
            camera,
            projectionHelper.value,
            rect,
            at.clientX,
            at.clientY,
            EARTH_RADIUS_METERS
          )
        : null;
  }

  function invertFlatProjection(intersection: THREE.Intersection) {
    const projection = projectionHelper.value.getD3Projection();
    const inverted = projection?.invert?.([
      intersection.point.x,
      -intersection.point.y,
    ]);
    if (
      !inverted ||
      !Number.isFinite(inverted[0]) ||
      !Number.isFinite(inverted[1])
    ) {
      return null;
    }

    const [lon, lat] = inverted;
    const [projectedX, projectedY] = projectionHelper.value.project(lat, lon);
    if (
      !Number.isFinite(projectedX) ||
      !Number.isFinite(projectedY) ||
      Math.abs(projectedX - intersection.point.x) > 1e-3 ||
      Math.abs(projectedY - intersection.point.y) > 1e-3
    ) {
      return null;
    }

    return { lat, lon: ProjectionHelper.normalizeLongitude(lon) };
  }

  function getHoveredGeoPoint(clientX: number, clientY: number) {
    if (!canvas.value || !camera || !pickSurface) {
      return null;
    }

    const rect = canvas.value.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return null;
    }

    const pointer = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1
    );
    raycaster.setFromCamera(pointer, camera);

    if (!projectionHelper.value.isFlat) {
      // The exact sphere, not the pick mesh: a 64-segment sphere's facets sit
      // kilometres below the surface, and a ray off the view axis crosses
      // that gap sideways (hundreds of metres, a screen at a 2 km altitude).
      const hit = raycaster.ray.intersectSphere(
        GLOBE_SPHERE,
        new THREE.Vector3()
      );
      if (!hit) {
        return null;
      }
      const { lat, lon } = ProjectionHelper.cartesianToLatLon(
        hit.x,
        hit.y,
        hit.z
      );
      return { lat, lon, screenX: clientX, screenY: clientY };
    }

    const [intersection] = raycaster.intersectObject(pickSurface, false);
    if (!intersection) {
      return null;
    }

    const geo = invertFlatProjection(intersection);
    if (!geo) {
      return null;
    }
    return { ...geo, screenX: clientX, screenY: clientY };
  }

  function isHoverActive() {
    return (
      store.hoverEnabled &&
      projectionHelper.value.type !== PROJECTION_TYPES.AZIMUTHAL_HYBRID
    );
  }

  function refreshHover() {
    if (!isHoverActive()) {
      hoveredGeoPoint.value = null;
      return;
    }

    if (!lastPointerPosition) {
      hoveredGeoPoint.value = null;
      return;
    }

    hoveredGeoPoint.value = getHoveredGeoPoint(
      lastPointerPosition.clientX,
      lastPointerPosition.clientY
    );
  }

  function getProjectedBounds() {
    const helper = projectionHelper.value;

    if (!helper.isFlat) {
      return {
        minX: -Math.PI,
        maxX: Math.PI,
        minY: -Math.PI / 2,
        maxY: Math.PI / 2,
        width: 2 * Math.PI,
        height: Math.PI,
        centerX: 0,
        centerY: 0,
      };
    }

    const projection = helper.getD3Projection();
    const path = d3.geoPath(projection);

    const [[minX, minY], [maxX, maxY]] = path.bounds({ type: "Sphere" });

    return {
      minX,
      maxX,
      minY,
      maxY,
      width: maxX - minX,
      height: maxY - minY,
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
    };
  }

  function disposeObject3D(object: THREE.Object3D) {
    const geometries = new Set<THREE.BufferGeometry>();
    const materials = new Set<THREE.Material>();

    object.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) {
        return;
      }
      geometries.add(child.geometry);
      if (Array.isArray(child.material)) {
        child.material.forEach((material) => materials.add(material));
      } else {
        materials.add(child.material);
      }
    });

    geometries.forEach((geometry) => geometry.dispose());
    materials.forEach((material) => material.dispose());
  }

  function cleanupSurface(mesh: THREE.Object3D | undefined) {
    if (!scene || !mesh) {
      return;
    }
    scene.remove(mesh);
    disposeObject3D(mesh);
  }

  function makePickMaterial(doubleSided = false) {
    const material = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      side: doubleSided ? THREE.DoubleSide : THREE.FrontSide,
    });
    material.depthWrite = false;
    material.colorWrite = false;
    return material;
  }

  function appendBoundaryPoint(points: THREE.Vector2[], x: number, y: number) {
    const point = new THREE.Vector2(x, y);
    const previous = points[points.length - 1];
    if (previous && previous.distanceToSquared(point) < 1e-10) {
      return;
    }
    points.push(point);
  }

  function projectBoundaryPoint(
    projection: d3.GeoProjection,
    bounds: ReturnType<typeof getProjectedBounds>,
    points: THREE.Vector2[],
    lon: number,
    lat: number
  ) {
    const projected = projection([lon, lat]);
    if (
      !projected ||
      !Number.isFinite(projected[0]) ||
      !Number.isFinite(projected[1])
    ) {
      return;
    }
    appendBoundaryPoint(
      points,
      projected[0] - bounds.centerX,
      -projected[1] - bounds.centerY
    );
  }

  function createFlatBoundaryPoints(
    bounds: ReturnType<typeof getProjectedBounds>
  ) {
    const helper = projectionHelper.value;
    if (isAzimuthalProjectionType(helper.type)) {
      return [];
    }

    const projection = new ProjectionHelper(helper.type, {
      lat: 0,
      lon: 0,
    }).getD3Projection();
    if (!projection) {
      return [];
    }

    const points: THREE.Vector2[] = [];
    const maxLat =
      helper.type === PROJECTION_TYPES.MERCATOR ? MERCATOR_LAT_LIMIT : 90;

    for (let lon = -180; lon <= 180; lon += FLAT_BOUNDARY_STEP_DEGREES) {
      projectBoundaryPoint(projection, bounds, points, lon, maxLat);
    }
    for (
      let lat = maxLat - FLAT_BOUNDARY_STEP_DEGREES;
      lat >= -maxLat;
      lat -= FLAT_BOUNDARY_STEP_DEGREES
    ) {
      projectBoundaryPoint(projection, bounds, points, 180, lat);
    }
    for (
      let lon = 180 - FLAT_BOUNDARY_STEP_DEGREES;
      lon >= -180;
      lon -= FLAT_BOUNDARY_STEP_DEGREES
    ) {
      projectBoundaryPoint(projection, bounds, points, lon, -maxLat);
    }
    for (
      let lat = -maxLat + FLAT_BOUNDARY_STEP_DEGREES;
      lat <= maxLat;
      lat += FLAT_BOUNDARY_STEP_DEGREES
    ) {
      projectBoundaryPoint(projection, bounds, points, -180, lat);
    }

    return points;
  }

  function createFlatCropGeometry(
    bounds: ReturnType<typeof getProjectedBounds>,
    scaledWidth: number,
    scaledHeight: number
  ) {
    const boundaryPoints = createFlatBoundaryPoints(bounds);
    if (boundaryPoints.length < 3) {
      return undefined;
    }

    const halfWidth = scaledWidth / 2;
    const halfHeight = scaledHeight / 2;
    const outerShape = new THREE.Shape([
      new THREE.Vector2(-halfWidth, -halfHeight),
      new THREE.Vector2(halfWidth, -halfHeight),
      new THREE.Vector2(halfWidth, halfHeight),
      new THREE.Vector2(-halfWidth, halfHeight),
    ]);
    outerShape.holes.push(new THREE.Path(boundaryPoints));
    return new THREE.ShapeGeometry(outerShape);
  }

  function createBackgroundMaterial() {
    return new THREE.MeshBasicMaterial({
      color: 0x000000,
      side: THREE.DoubleSide,
    });
  }

  function createFlatSurfaces() {
    const bounds = getProjectedBounds();
    const width = Math.max(bounds.width, 1);
    const height = Math.max(bounds.height, 1);
    const scaledWidth = width * 1.05;
    const scaledHeight = height * 1.05;
    const backgroundMaterial = createBackgroundMaterial();
    baseSurface = new THREE.Mesh(
      new THREE.PlaneGeometry(scaledWidth, scaledHeight),
      backgroundMaterial
    );
    baseSurface.renderOrder = -10;
    baseSurface.position.set(bounds.centerX, bounds.centerY, -0.05);

    const cropGeometry = createFlatCropGeometry(
      bounds,
      scaledWidth,
      scaledHeight
    );
    if (cropGeometry) {
      const cropMaterial = createBackgroundMaterial();
      // Draw the crop over the map without moving it toward the camera.
      cropMaterial.transparent = true;
      cropMaterial.depthTest = false;
      cropMaterial.depthWrite = false;
      const cropSurface = new THREE.Mesh(cropGeometry, cropMaterial);
      cropSurface.renderOrder = FLAT_CROP_RENDER_ORDER;
      cropSurface.position.z = -baseSurface.position.z;
      baseSurface.add(cropSurface);
    }

    pickSurface = new THREE.Mesh(
      new THREE.PlaneGeometry(scaledWidth, scaledHeight),
      makePickMaterial(true)
    );
    pickSurface.position.set(bounds.centerX, bounds.centerY, 0);
  }

  function createGlobeSurfaces() {
    const backgroundMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
    });
    baseSurface = new THREE.Mesh(
      new THREE.SphereGeometry(0.99, 64, 64),
      backgroundMaterial
    );
    baseSurface.renderOrder = -10;
    baseSurface.rotation.x = Math.PI / 2;

    pickSurface = new THREE.Mesh(
      new THREE.SphereGeometry(1.0, 64, 64),
      makePickMaterial()
    );
    pickSurface.rotation.x = Math.PI / 2;
  }

  function updateBaseSurface() {
    if (!scene) {
      return;
    }

    cleanupSurface(baseSurface);
    cleanupSurface(pickSurface);
    baseSurface = undefined;
    pickSurface = undefined;

    if (projectionHelper.value.isFlat) {
      createFlatSurfaces();
    } else {
      createGlobeSurfaces();
    }

    if (baseSurface) {
      scene.add(baseSurface);
    }
    if (pickSurface) {
      scene.add(pickSurface);
    }
  }

  function configureFlatProjectionCamera(
    cam: THREE.PerspectiveCamera,
    controls: OrbitControls
  ) {
    const bounds = getProjectedBounds();

    // Compute the tightest distance that keeps the full projection visible,
    // accounting for the camera aspect ratio.
    const vHalfFov = THREE.MathUtils.degToRad(
      (cam.fov ?? CAMERA_VERTICAL_FOV_DEGREES) / 2
    );
    const hHalfFov = Math.atan(Math.tan(vHalfFov) * cam.aspect);
    const zForHeight = getCameraDistanceForVerticalSpan(bounds.height, cam.fov);
    const zForWidth = bounds.width / 2 / Math.tan(hHalfFov);
    const targetDistance = Math.max(zForHeight, zForWidth);

    cam.up.set(0, 1, 0);
    cam.quaternion.identity();
    cam.rotation.set(0, 0, 0);

    cam.near = 0.005;
    cam.far = 200;
    cam.updateProjectionMatrix();

    cam.position.set(
      bounds.centerX,
      bounds.centerY,
      Math.max(targetDistance * 1.1, 3)
    );
    controls.target.set(bounds.centerX, bounds.centerY, 0);

    controls.enablePan = true;
    controls.enableRotate = false;
    controls.enableDamping = false;
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.PAN,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.ROTATE,
    };
    controls.minDistance = 0.001;
    controls.maxDistance = 200;
  }

  function configureGlobeProjectionCamera(
    cam: THREE.PerspectiveCamera,
    controls: OrbitControls
  ) {
    // Compute the tightest distance at which the globe (radius 1) still fits
    // fully within the viewport on both axes, with a small 5 % margin.
    const targetDistance = getGlobeFitCameraDistance(cam.aspect, cam.fov);

    cam.up.set(0, 0, 1);
    cam.near = 0.1;
    cam.far = 1000;
    cam.updateProjectionMatrix();
    controls.enablePan = false;
    controls.enableRotate = true;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN,
    };
    controls.minDistance = GLOBE_MIN_CAMERA_DISTANCE;
    controls.maxDistance = 1000;
    applyProjectionCenterToGlobeCamera(cam, controls, targetDistance);
  }

  function getProjectionCenterCameraPosition(distance: number) {
    const center = projectionCenter.value ?? DEFAULT_PROJECTION_CENTER;
    const latRad = THREE.MathUtils.degToRad(center.lat);
    const lonRad = THREE.MathUtils.degToRad(center.lon);

    return new THREE.Vector3(
      Math.cos(latRad) * Math.cos(lonRad),
      Math.cos(latRad) * Math.sin(lonRad),
      Math.sin(latRad)
    ).multiplyScalar(distance);
  }

  function applyProjectionCenterToGlobeCamera(
    cam: THREE.PerspectiveCamera,
    controls: OrbitControls,
    distance: number
  ) {
    cam.position.copy(
      getProjectionCenterCameraPosition(
        Math.max(distance, controls.minDistance)
      )
    );
    controls.target.set(0, 0, 0);
    cam.lookAt(controls.target);
    controls.update();
  }

  function syncProjectionCenterFromCamera(cam: THREE.PerspectiveCamera) {
    const center = ProjectionHelper.cartesianToLatLon(
      cam.position.x,
      cam.position.y,
      cam.position.z
    );
    const nextCenter = {
      lat: roundProjectionCenter(Math.max(-90, Math.min(90, center.lat))),
      lon: roundProjectionCenter(
        ProjectionHelper.normalizeLongitude(center.lon)
      ),
    };
    const currentCenter = projectionCenter.value ?? DEFAULT_PROJECTION_CENTER;
    if (
      currentCenter.lat === nextCenter.lat &&
      currentCenter.lon === nextCenter.lon
    ) {
      return;
    }

    updatingProjectionCenterFromCamera = true;
    projectionCenter.value = nextCenter;
    queueMicrotask(() => {
      updatingProjectionCenterFromCamera = false;
    });
  }

  function applyUrlCameraState(
    cam: THREE.PerspectiveCamera,
    controls: OrbitControls,
    state: TCameraUrlState
  ) {
    const distance = Math.max(
      altitudeToCameraDistance(state.alt, projectionHelper.value.isFlat),
      controls.minDistance
    );
    if (projectionHelper.value.isFlat) {
      cam.quaternion.identity();
      cam.rotation.set(0, 0, 0);
      const x = state.px / EARTH_RADIUS_METERS;
      const y = state.py / EARTH_RADIUS_METERS;
      cam.position.set(x, y, distance);
      controls.target.set(x, y, 0);
      applyCameraTarget(cam, controls);
      return;
    }

    applyProjectionCenterToGlobeCamera(cam, controls, distance);
  }

  function applyCameraTarget(
    cam: THREE.PerspectiveCamera,
    controls: OrbitControls
  ) {
    const target = controls.target.clone();
    cam.lookAt(target);
    cam.updateProjectionMatrix();
    controls.update();
  }

  function syncCameraStateWithUrl(
    cam: THREE.PerspectiveCamera,
    controls: OrbitControls
  ) {
    const state = cameraState.decodeCameraFromURL();
    if (state) {
      applyUrlCameraState(cam, controls, state);
      return;
    }

    cameraState.encodeCameraToURL(cam, projectionHelper.value.isFlat);
  }

  function configureCameraForProjection() {
    const cam = getCamera();
    const controls = getOrbitControls();
    if (!cam || !controls) {
      return;
    }

    // the globe orbit is configured from its own settings
    pivotControls?.leave();
    if (projectionHelper.value.isFlat) {
      configureFlatProjectionCamera(cam, controls);
      controls.autoRotate = false;
    } else {
      configureGlobeProjectionCamera(cam, controls);
      controls.autoRotate = store.isRotating;
    }

    applyCameraTarget(cam, controls);
    syncCameraStateWithUrl(cam, controls);
    applyPointPivot();

    // In display mode, disable all direct interaction after camera is set up
    if (isDisplayMode.value) {
      controls.enabled = false;
    }

    updateCameraForPanel();
    redraw();

    if (store.isRotating) {
      animationLoop();
    }
  }

  // set the camera from external preset (e.g. presenter sync) and apply to controls
  function applyCameraPreset(data: TCameraState) {
    initialRegionalFitPending = false;
    const cam = getCamera();
    const controls = getOrbitControls();
    if (!cam || !controls) {
      return;
    }

    cameraState.applyCameraState(cam, data);
    if (projectionHelper.value.isFlat) {
      controls.target.set(cam.position.x, cam.position.y, 0);
    } else {
      controls.target.set(0, 0, 0);
      syncProjectionCenterFromCamera(cam);
    }
    controls.update();
    cameraState.encodeCameraToURL(cam, projectionHelper.value.isFlat);
    redraw();
  }

  function fitCameraToDataset(objects: { geometry: THREE.BufferGeometry }[]) {
    if (
      !initialRegionalFitPending ||
      !camera ||
      !orbitControls ||
      !objects.length
    ) {
      return;
    }
    initialRegionalFitPending = false;
    if (projectionHelper.value.isFlat) {
      return;
    }
    const rect = box.value?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      camera.aspect = rect.width / rect.height;
    }
    const position = getRegionalCameraPosition(
      objects.map((object) => object.geometry),
      camera
    );
    if (!position) {
      return;
    }
    camera.position
      .copy(position)
      .setLength(Math.max(position.length(), orbitControls.minDistance));
    orbitControls.target.set(0, 0, 0);
    applyCameraTarget(camera, orbitControls);
    syncProjectionCenterFromCamera(camera);
    cameraState.encodeCameraToURL(camera, false);
    redraw();
  }

  function initEssentials() {
    scene = new THREE.Scene();
    const center = new THREE.Vector3();
    camera = new THREE.PerspectiveCamera(
      CAMERA_VERTICAL_FOV_DEGREES,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    renderer = new THREE.WebGLRenderer({
      canvas: canvas.value,
      powerPreference: "high-performance",
    });

    camera.up = new THREE.Vector3(0, 0, 1);
    camera.position.x = 30;
    camera.lookAt(center);

    orbitControls = new OrbitControls(camera, renderer.domElement);
    // the pivot wraps first: the surface zoom stands aside while it is on;
    // a pivot something else moved off the sphere ends the point tier
    pivotControls = createPointPivot(orbitControls, () => {
      pivotWanted = false;
      store.setPointTier(false);
    });
    useSurfaceZoom(
      orbitControls,
      () => projectionHelper.value.isFlat || pivotControls!.active
    );
    orbitControls.minDistance = GLOBE_MIN_CAMERA_DISTANCE;
    orbitControls.enablePan = false;

    updateBaseSurface();
    configureCameraForProjection();
  }

  function updateCameraForPanel() {
    const camera = getCamera();
    if (!camera || !box.value) {
      return;
    }

    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    const panelWidth =
      !isMobile && controlPanelVisible.value ? CONTROL_PANEL_WIDTH : 0;
    const { width: boxWidth } = box.value.getBoundingClientRect();

    targetOffset = panelWidth > 0 ? panelWidth / boxWidth / 2 : 0;
    currentOffset = init ? targetOffset : currentOffset;
    init = false;

    animateCamera();
  }

  function animateCamera() {
    const camera = getCamera();
    if (!camera || !box.value) {
      return;
    }

    currentOffset = THREE.MathUtils.lerp(currentOffset, targetOffset, 0.1);

    if (Math.abs(targetOffset - currentOffset) < 0.001) {
      currentOffset = targetOffset;
    }

    const aspect = camera.aspect;
    const fov = THREE.MathUtils.degToRad(camera.fov);
    const near = camera.near;
    const far = camera.far;

    const top = near * Math.tan(fov / 2);
    const bottom = -top;
    const right = top * aspect;
    const left = -right;

    camera.projectionMatrix.makePerspective(
      left,
      right,
      top,
      bottom,
      near,
      far
    );

    redraw();

    if (currentOffset !== targetOffset) {
      requestAnimationFrame(animateCamera);
    }
  }

  function onCanvasResize() {
    if (!box.value) {
      return;
    }
    const { width: boxWidth, height: boxHeight } =
      box.value.getBoundingClientRect();

    if (boxWidth !== width.value || boxHeight !== height.value) {
      const aspect = boxWidth / boxHeight;
      getCamera()!.aspect = aspect;
      getCamera()!.updateProjectionMatrix();

      const myRenderer = getRenderer() as THREE.WebGLRenderer;
      if (myRenderer) {
        myRenderer.setSize(boxWidth, boxHeight);
      }

      width.value = boxWidth;
      height.value = boxHeight;

      updateCameraForPanel();
      redraw();
    }
  }

  function startProjectionDrag(clientX: number, clientY: number) {
    if (!projectionHelper.value.isFlat || isDisplayMode.value) {
      return false;
    }
    projectionDragActive = true;
    dragStartX = clientX;
    dragStartY = clientY;
    const center = projectionCenter.value ?? { lat: 0, lon: 0 };
    dragStartCenterLon = center.lon;
    dragStartCenterLat = center.lat;
    return true;
  }

  function updateProjectionDrag(clientX: number, clientY: number) {
    if (!projectionDragActive || !projectionHelper.value.isFlat) {
      return;
    }

    const deltaX = clientX - dragStartX;
    const deltaY = clientY - dragStartY;

    const canvasWidth = width.value ?? 800;
    const canvasHeight = height.value ?? 600;

    const lonSensitivity = 180 / canvasWidth;
    const latSensitivity = 90 / canvasHeight;

    let newLon = dragStartCenterLon + deltaX * lonSensitivity;
    let newLat = dragStartCenterLat - deltaY * latSensitivity;

    newLat = Math.round(Math.max(-90, Math.min(90, newLat)) * 100) / 100;
    newLon =
      Math.round(ProjectionHelper.normalizeLongitude(newLon) * 100) / 100;

    projectionCenter.value = { lat: newLat, lon: newLon };
  }

  function handleRightMouseDown(event: MouseEvent) {
    if (event.button !== 2) {
      return;
    }
    if (startProjectionDrag(event.clientX, event.clientY)) {
      event.preventDefault();
    }
  }

  function handleRightMouseMove(event: MouseEvent) {
    if (projectionDragActive) {
      event.preventDefault();
      updateProjectionDrag(event.clientX, event.clientY);
    }
  }

  function toggleRotate() {
    store.toggleRotating();
    if (!projectionHelper.value.isFlat) {
      getOrbitControls()!.autoRotate = store.isRotating;
    }
    animationLoop();
  }

  function syncProjectionCenterAfterCameraChange(
    cam: THREE.PerspectiveCamera | undefined,
    controlsUpdated: boolean,
    userInteractionActive: boolean
  ) {
    if (
      cam &&
      !projectionHelper.value.isFlat &&
      (controlsUpdated || userInteractionActive || store.isRotating)
    ) {
      syncProjectionCenterFromCamera(cam);
    }
  }

  function runAnimationCallbacks(timestamp: number) {
    const deltaSeconds = Math.min(
      Math.max((timestamp - (lastAnimationTime ?? timestamp)) / 1000, 0),
      0.1
    );
    lastAnimationTime = timestamp;
    for (const callback of animationCallbacks) {
      callback(deltaSeconds);
    }
  }

  function updateRotationSpeed() {
    if (
      projectionHelper.value.isFlat ||
      pivotControls?.active ||
      !orbitControls ||
      !camera
    ) {
      return;
    }
    // Normalize by the visible span so navigation sensitivity remains tied to
    // apparent zoom instead of changing when the camera lens changes.
    const normalizedDistance =
      getVisibleVerticalSpan(camera.position.length(), camera.fov) / 4;
    orbitControls.rotateSpeed =
      Math.min(1, 0.01 + normalizedDistance ** 2) *
      getGlobeMovementScale(camera.position.length());
  }

  function updateFlatProjectionRotation() {
    if (!store.isRotating || !projectionHelper.value.isFlat) {
      return;
    }
    const center = projectionCenter.value ?? DEFAULT_PROJECTION_CENTER;
    const newLon = ProjectionHelper.normalizeLongitude(center.lon - 0.3);
    projectionCenter.value = { lat: center.lat, lon: newLon };
  }

  function syncMotionState(
    controlsUpdated: boolean,
    userInteractionActive: boolean
  ) {
    setMotionState(
      userInteractionActive || store.isRotating || controlsUpdated
    );
  }

  function animationLoop(timestamp = performance.now()) {
    cancelAnimationFrame(frameId.value);
    runAnimationCallbacks(timestamp);

    updateFlatProjectionRotation();
    updateRotationSpeed();

    const controlsUpdated = render();
    const userInteractionActive = mouseDown || wheelActive;
    syncMotionState(controlsUpdated, userInteractionActive);
    if (lastPointerPosition) {
      refreshHover();
    }
    const cam = getCamera();
    syncProjectionCenterAfterCameraChange(
      cam,
      controlsUpdated,
      userInteractionActive
    );
    if (!userInteractionActive && !store.isRotating) {
      if (controlsUpdated) {
        // Controls are still moving (damping draining) – reset idle counter.
        idleFrameCount = 0;
      } else {
        idleFrameCount++;
      }
      if (cam && isPresenterActive.value && !store.isRotating) {
        cameraState.encodeCameraToURL(cam, projectionHelper.value.isFlat);
      }
      if (idleFrameCount >= IDLE_FRAMES_BEFORE_STOP) {
        // Save the settled camera even when layer animations keep running.
        setMotionState(false);
        if (cam && idleFrameCount === IDLE_FRAMES_BEFORE_STOP) {
          cameraState.debouncedEncodeCameraToURL(
            cam,
            projectionHelper.value.isFlat
          );
        }
        if (animationCallbacks.size === 0) {
          idleFrameCount = 0;
          return;
        }
      }
    } else {
      idleFrameCount = 0;
      if (isPresenterActive.value && cam && userInteractionActive) {
        cameraState.encodeCameraToURL(cam, projectionHelper.value.isFlat);
      }
    }
    frameId.value = requestAnimationFrame(animationLoop);
  }

  function onInteractionStart() {
    initialRegionalFitPending = false;
    mouseDown = true;
    idleFrameCount = 0;
    setMotionState(true);
    animationLoop();
  }

  function onInteractionEnd() {
    mouseDown = false;
    animationLoop();
  }

  function onWheelInteraction() {
    initialRegionalFitPending = false;
    wheelActive = true;
    idleFrameCount = 0;
    setMotionState(true);
    debouncedEndWheelInteraction();
    animationLoop();
  }

  // On the pivot camera the wheel zooms toward the ground under the cursor;
  // OrbitControls' own zoom is off there.
  function onPivotWheel(event: WheelEvent) {
    const rect = canvas.value?.getBoundingClientRect();
    if (!pivotControls?.active || !orbitControls || !rect) {
      return;
    }
    event.preventDefault();
    pivotControls.zoom(
      wheelDollyScale(event, orbitControls.zoomSpeed),
      new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      )
    );
  }

  function updateHoverPosition(clientX: number, clientY: number) {
    lastPointerPosition = { clientX, clientY };
    refreshDistanceScale();
    refreshHover();
  }

  function onTouchPickStart(event: TouchEvent) {
    const touch = event.touches[0];
    touchPickStart =
      event.touches.length === 1 && touch
        ? { clientX: touch.clientX, clientY: touch.clientY }
        : null;
    touchPickMoved = false;
  }

  function onTouchPickMove(event: TouchEvent) {
    const touch = event.touches[0];
    if (!touchPickStart || !touch) {
      return;
    }
    const deltaX = touch.clientX - touchPickStart.clientX;
    const deltaY = touch.clientY - touchPickStart.clientY;
    if (
      deltaX * deltaX + deltaY * deltaY >
      TOUCH_PICK_TAP_MAX_DISTANCE_PX * TOUCH_PICK_TAP_MAX_DISTANCE_PX
    ) {
      touchPickMoved = true;
    }
  }

  function onTouchPickEnd(event: TouchEvent) {
    const touch = event.changedTouches[0];
    if (!touchPickStart || touchPickMoved || !touch) {
      touchPickStart = null;
      return;
    }
    updateHoverPosition(touch.clientX, touch.clientY);
    clickedGeoPoint.value = getHoveredGeoPoint(touch.clientX, touch.clientY);
    touchPickStart = null;
  }

  function onClickEnd(event: MouseEvent) {
    const start = clickStart;
    clickStart = null;
    if (!start || event.button !== 0) {
      return;
    }
    const deltaX = event.clientX - start.clientX;
    const deltaY = event.clientY - start.clientY;
    if (
      deltaX * deltaX + deltaY * deltaY <=
      TOUCH_PICK_TAP_MAX_DISTANCE_PX * TOUCH_PICK_TAP_MAX_DISTANCE_PX
    ) {
      clickedGeoPoint.value = getHoveredGeoPoint(event.clientX, event.clientY);
    }
  }

  function setupHoverListeners() {
    useEventListener(
      canvas.value,
      "mousemove",
      (event: MouseEvent) => {
        updateHoverPosition(event.clientX, event.clientY);
      },
      { passive: true }
    );

    useEventListener(
      canvas.value,
      "mousedown",
      (event: MouseEvent) => {
        clickStart =
          event.button === 0
            ? { clientX: event.clientX, clientY: event.clientY }
            : null;
      },
      { passive: true }
    );
    useEventListener(canvas.value, "mouseup", onClickEnd, { passive: true });
    useEventListener(canvas.value, "touchstart", onTouchPickStart, {
      passive: true,
    });
    useEventListener(canvas.value, "touchmove", onTouchPickMove, {
      passive: true,
    });
    useEventListener(canvas.value, "touchend", onTouchPickEnd, {
      passive: true,
    });
    useEventListener(canvas.value, "touchcancel", () => {
      touchPickStart = null;
    });

    useEventListener(
      canvas.value,
      "mouseleave",
      () => {
        lastPointerPosition = null;
        hoveredGeoPoint.value = null;
        if (!pivotControls?.active) {
          store.distanceScale = null;
        }
      },
      { passive: true }
    );
  }

  function setupInteractionListeners() {
    setupHoverListeners();

    useEventListener(canvas.value, "wheel", onWheelInteraction, {
      passive: true,
    });
    useEventListener(canvas.value, "wheel", onPivotWheel, { passive: false });

    useEventListener(canvas.value, "mouseup", onInteractionEnd, {
      passive: true,
    });
    useEventListener(canvas.value, "mousedown", onInteractionStart, {
      passive: true,
    });
    useEventListener(canvas.value, "touchstart", onInteractionStart, {
      passive: true,
    });
    useEventListener(canvas.value, "touchend", onInteractionEnd, {
      passive: true,
    });
  }

  // Setup right-click drag listeners for projection center adjustment
  function setupRightClickListeners() {
    useEventListener(
      canvas.value,
      "mousedown",
      (e: MouseEvent) => {
        if (e.button === 2) {
          handleRightMouseDown(e);
        }
      },
      { passive: false }
    );

    useEventListener(
      canvas.value,
      "mousemove",
      (e: MouseEvent) => {
        if (projectionDragActive) {
          handleRightMouseMove(e);
        }
      },
      { passive: false }
    );

    useEventListener(
      canvas.value,
      "mouseup",
      (e: MouseEvent) => {
        if (e.button === 2) {
          projectionDragActive = false;
        }
      },
      { passive: false }
    );
  }

  // Setup touch drag listeners for projection center adjustment
  function setupTouchProjectionListeners() {
    useEventListener(
      canvas.value,
      "touchstart",
      (e: TouchEvent) => {
        const shouldPrevent =
          e.touches.length === 1 &&
          startProjectionDrag(e.touches[0].clientX, e.touches[0].clientY);
        if (shouldPrevent) {
          e.preventDefault();
        }
      },
      { passive: false }
    );

    useEventListener(
      canvas.value,
      "touchmove",
      (e: TouchEvent) => {
        if (e.touches.length === 1 && projectionDragActive) {
          e.preventDefault();
          updateProjectionDrag(e.touches[0].clientX, e.touches[0].clientY);
        }
      },
      { passive: false }
    );

    useEventListener(
      canvas.value,
      "touchend",
      () => {
        projectionDragActive = false;
      },
      { passive: false }
    );
  }

  const projectionArrowKeys = [
    "ArrowRight",
    "ArrowLeft",
    "ArrowUp",
    "ArrowDown",
  ];

  // Setup keyboard navigation listeners
  function setupKeyboardListeners() {
    if (isDisplayMode.value) {
      // Disable keyboard navigation in display/presenter mode to avoid
      // interfering with presenter controls
      return;
    }
    useEventListener(box.value, "keydown", (e: KeyboardEvent) => {
      const navigationKeys = [...projectionArrowKeys, "+", "-"];

      if (navigationKeys.includes(e.key)) {
        onInteractionStart();
        // on the pivot camera the arrows pan and +/- dolly, as on a flat map
        handleKeyDown(
          e,
          getOrbitControls()!,
          projectionHelper.value.isFlat || Boolean(pivotControls?.active)
        );
        onInteractionEnd();
      }
    });
  }

  onMounted(() => {
    mouseDown = false;

    setupInteractionListeners();
    setupRightClickListeners();
    setupTouchProjectionListeners();
    setupKeyboardListeners();

    initEssentials();
    void onReady?.();
  });

  useResizeObserver(box, onCanvasResize);

  onBeforeUnmount(() => {
    store.distanceScale = null;
    if (frameId.value) {
      cancelAnimationFrame(frameId.value);
      frameId.value = 0;
    }
    animationCallbacks.clear();
    orbitControls?.dispose();
    orbitControls = undefined;
    cleanupSurface(baseSurface);
    cleanupSurface(pickSurface);
    baseSurface = undefined;
    pickSurface = undefined;
    scene?.clear();
    camera?.clear();
    renderer?.dispose();
    scene = undefined;
    renderer = undefined;
    camera = undefined;
  });

  watch(
    () => controlPanelVisible.value,
    () => {
      updateCameraForPanel();
      refreshHover();
    }
  );

  // Sync rotation when store.isRotating is changed externally (e.g. presenter mode)
  watch(
    () => store.isRotating,
    (rotating) => {
      if (!projectionHelper.value.isFlat) {
        const oc = getOrbitControls();
        if (oc) {
          oc.autoRotate = rotating;
        }
      }
      animationLoop();
    }
  );

  watch(
    () => projectionCenter.value,
    () => {
      initialRegionalFitPending = false;
      if (
        projectionHelper.value.isFlat ||
        updatingProjectionCenterFromCamera ||
        // the pivot camera owns the view; its centre follows the camera
        pivotControls?.active
      ) {
        return;
      }
      const cam = getCamera();
      const controls = getOrbitControls();
      if (!cam || !controls) {
        return;
      }
      applyProjectionCenterToGlobeCamera(cam, controls, cam.position.length());
      cameraState.encodeCameraToURL(cam, projectionHelper.value.isFlat);
      redraw();
    },
    { deep: true }
  );

  watch(
    () => [
      urlParameterStore.paramCameraPx,
      urlParameterStore.paramCameraPy,
      urlParameterStore.paramCameraAlt,
    ],
    () => {
      // This watcher is only relevant in presenter display mode where camera state is synced via URL.
      // The controller display writes the URL on every camera change, and the
      // display reacts to URL changes by applying the camera state.
      if (!isDisplayMode.value) {
        return;
      }
      const cam = getCamera();
      const controls = getOrbitControls();
      if (!cam || !controls) {
        return;
      }
      const state = cameraState.decodeCameraFromURL();
      if (!state) {
        return;
      }
      applyUrlCameraState(cam, controls, state);
      redraw();
    }
  );

  watch(
    [() => projectionHelper.value.type, () => projectionCenter.value],
    () => {
      refreshDistanceScale();
      refreshHover();
    },
    { deep: true }
  );

  watch(
    () => store.hoverEnabled,
    (enabled) => {
      if (!enabled) {
        hoveredGeoPoint.value = null;
        return;
      }
      refreshHover();
    }
  );

  const { makeSnapshot } = useGridSnapshot({
    canvas,
    getRenderer,
    getScene,
    getCamera,
    getBaseSurface,
    render,
  });

  return {
    canvas,
    box,
    getScene,
    getCamera,
    getRenderer,
    redraw,
    toggleRotate,
    makeSnapshot,
    applyCameraPreset,
    fitCameraToDataset,
    registerUpdateLOD,
    registerAnimationCallback,
    updateBaseSurface,
    configureCameraForProjection,
    hoveredGeoPoint,
    clickedGeoPoint,
    pointPivot,
  };
}
